// NodeTimelinesBar.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  buildViewSelectorOptions,
  getActiveSelectorLabel,
  getActiveSelectorOption,
} from 'utilities/ViewSelectorOptions';
import { getTimelineViewTypeIconKey } from 'utilities/ViewTypeIcons';

// CSS
import './NodeTimelinesBar.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Popup from 'components/popup/Popup';
import TimelinesPopupContents from 'node/timelines/popup/TimelinesPopupContents';

/**
 * NodeTimelinesBar
 * 
 * Renders the views bar for a node, allowing users to switch between different views.
 * 
 * @param {object[]} views - Optional array of timeline view options.
 * 
 * @returns {ReactNode} NodeTimelinesBar component.
 */
const DEFAULT_TIMELINE_VIEWS = [
  { id: 'DAY', label: 'Day', type: 'DAY' },
  { id: 'WEEK', label: 'Week', type: 'WEEK' },
  { id: 'MONTH', label: 'Month', type: 'MONTH' },
  { id: 'QUARTER', label: 'Quarter', type: 'QUARTER' },
  { id: 'YEAR', label: 'Year', type: 'YEAR' },
];

const NodeTimelinesBar = React.memo(({ views }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  const { timelineViewType, setTimelineViewType } = useCalendarTimeline();

  // ----- REFS ----- //

  const anchorRef = useRef(null);

  // ----- STATE ----- //

  const [popupOpen, setPopupOpen] = useState(false);

  // ----- MEMOS ----- //

  /**
   * Memoize the options for the select input.
   */

  const options = useMemo(() => {
    if (Array.isArray(views) && views.length > 0) {
      return buildViewSelectorOptions(views, { fallbackType: 'MONTH' });
    }
    return DEFAULT_TIMELINE_VIEWS;
  }, [views]);

  const activeViewLabel = useMemo(() => {
    return getActiveSelectorLabel(options, timelineViewType, 'Timeline View');
  }, [options, timelineViewType]);

  const activeViewIconKey = useMemo(() => {
    const match = getActiveSelectorOption(options, timelineViewType);
    return getTimelineViewTypeIconKey(match?.type);
  }, [options, timelineViewType]);

  // ----- HANDLERS ----- //

  /**
   * handleChange
   * 
   * Handle changes to the select input, calling the appropriate handler based on the selected option.
   * 
   * @param {Event} event - The change event from the select input.
   */
  /**
 * Handles change.
 *
 * @param {*} event
 */

  const handleChange = (event) => {
    const nextValue = event?.target?.value || '';
    if (nextValue) {
      setTimelineViewType(nextValue);
    }
  };

  /**
 * Handles popup select.
 *
 * @param {*} optionId
 */
  const handlePopupSelect = (optionId) => {
    setPopupOpen(false);
    handleChange({ target: { value: optionId } });
  };

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-timelines-bar-container">

        {/* VIEWS POPUP BUTTON */}
        <div
          className="node-timelines-bar-views-popup-button"
          ref={anchorRef}
          onClick={() => setPopupOpen((prev) => !prev)}
        >
          {activeViewIconKey ? (
            <Icon
              iconKey={activeViewIconKey}
              width={16}
              height={16}
              color={theme.foregroundMuted}
            />
          ) : null}

          {/* VIEWS POPUP BUTTON */}
          <div className="node-timelines-bar-views-popup-button-text">
            {activeViewLabel}
          </div>
          <span
            className="node-timelines-bar-views-popup-button-chevron"
            aria-hidden="true"
          >
            <Icon
              iconKey="keyboard_arrow_down"
              width={18}
              height={18}
              color={theme.foregroundMuted}
            />
          </span>
        </div>

      </div>

      <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>
        <TimelinesPopupContents
          options={options}
          selectedDateTypeId={timelineViewType}
          onSelectOption={handlePopupSelect}
        />
      </Popup>
    </>
  );
});

// Dev Tools
NodeTimelinesBar.displayName = 'NodeTimelinesBar';

export default NodeTimelinesBar;
